/**
 * Flash API classes used by the game that are not part of the runtime contract
 * (docs/contracts/flash-api.d.ts). Kept local to src/game until the Flash runtime provides them.
 */

/**
 * flash.net.URLVariables — here only used to parse the URL-encoded data strings embedded in the
 * offline build (tops, clans, assault zones, auction state). Dynamic class: decoded names become
 * own properties (repeated names become Arrays), in source order.
 * UNVERIFIED: decoding follows Shumway's implementation ('+' -> space, then unescape()); the
 * embedded data contains neither '+' nor '%'.
 */
export class URLVariables {
  [key: string]: any;

  constructor(source?: string | null) {
    if (source != null) this.decode(String(source));
  }

  decode(source: string): void {
    const pairs = source.split('&');
    for (const p of pairs) {
      const j = p.indexOf('=');
      if (j < 0) throw new Error('Error #2101: The String passed to URLVariables.decode() must be a URL-encoded query string containing name/value pairs.');
      const name = unescapeAS3(p.substring(0, j).split('+').join(' '));
      const value = unescapeAS3(p.substring(j + 1).split('+').join(' '));
      const existing = this[name];
      if (existing !== undefined && Object.prototype.hasOwnProperty.call(this, name)) {
        if (Array.isArray(existing)) existing.push(value);
        else this[name] = [existing, value];
      } else {
        this[name] = value;
      }
    }
  }

  toString(): string {
    const parts: string[] = [];
    for (const k of Object.keys(this)) {
      const v = this[k];
      const vs = Array.isArray(v) ? v : [v];
      for (const x of vs) parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(x))}`);
    }
    return parts.join('&');
  }
}

/** ECMA-262 Annex B unescape (%XX and %uXXXX). */
function unescapeAS3(s: string): string {
  return s.replace(/%u([0-9A-Fa-f]{4})|%([0-9A-Fa-f]{2})/g, (_m, u, x) => String.fromCharCode(parseInt(u ?? x, 16)));
}

let clipboardSink: (text: string) => void = () => {};

/**
 * flash.system.System.setClipboard (used by FWP.save_replay to copy the replay string).
 * The host decides what "clipboard" means (browser: navigator.clipboard; headless: capture).
 */
export function setClipboard(text: any): void {
  clipboardSink(String(text));
}

/** Installs the clipboard sink used by `setClipboard`. */
export function configureClipboard(sink: (text: string) => void): void {
  clipboardSink = sink;
}
